// UngespeichertDialog.tsx — Rückfrage „Ungespeicherte Änderungen" der
// Kontaktakte (Befund 28.09.2026): Speichern | Verwerfen | Abbrechen.
//
// Bewusst ohne Fluent und ohne window.confirm: die Popup-Schale der Akte
// `.kvp-rd.kvp-rd-scrim > .dm` (`contactRedesign.css`), per Portal an
// `document.body` mit eigener `.kvp-rd`-Hülle, über allen Fenstern.
//
// `useUngespeichertFrage` liefert die Frage als Promise — so kann jeder Weg
// aus der Akte heraus (Reiter, Fenster, Navigation, Status) schlicht
// `await frage()` schreiben, und der Dialog selbst kennt keinen davon.

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { Z_LAYERS } from "../windowManager/zLayers";
import "./contactRedesign.css";
import { alleDokumente } from "../windowManager/browserFenster/fensterRegister";

export type UngespeichertWahl = "speichern" | "verwerfen" | "abbrechen";

/**
 * Texte des Dialogs. Standard = Kontaktakte; das Mailfenster (T-212) nutzt
 * denselben Dialog mit „Als Entwurf speichern".
 */
export type UngespeichertTexte = {
  kicker: string;
  titel: string;
  text: string;
  speichern: string;
};

const STANDARD_TEXTE: UngespeichertTexte = {
  kicker: "Kontaktakte",
  titel: "Ungespeicherte Änderungen",
  text: "In dieser Akte sind Änderungen noch nicht gespeichert. Sollen sie gespeichert werden, bevor es weitergeht?",
  speichern: "Speichern",
};

const knopf: React.CSSProperties = {
  appearance: "none",
  border: "1.5px solid #ddd3dc",
  background: "#fff",
  color: "#591149",
  cursor: "pointer",
  font: '700 13.5px/1 "Hanken Grotesk", sans-serif',
  padding: "10px 16px",
  borderRadius: 10,
};
const primaer: React.CSSProperties = { ...knopf, background: "#7d2169", borderColor: "#7d2169", color: "#fff" };
const leise: React.CSSProperties = { ...knopf, border: "1.5px solid transparent", background: "transparent", color: "#6b5a66" };

export const UngespeichertDialog: React.FC<{
  offen: boolean;
  /** Läuft das Speichern gerade? Dann sind die Knöpfe gesperrt. */
  speichert?: boolean;
  onWahl: (wahl: UngespeichertWahl) => void;
  texte?: UngespeichertTexte;
}> = ({ offen, speichert = false, onWahl, texte = STANDARD_TEXTE }) => {
  const speichernRef = React.useRef<HTMLButtonElement | null>(null);

  React.useEffect(() => {
    if (!offen) return;
    speichernRef.current?.focus();
    const taste = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (!speichert) onWahl("abbrechen");
      }
    };
    alleDokumente.addEventListener("keydown", taste, true);
    return () => alleDokumente.removeEventListener("keydown", taste, true);
  }, [offen, speichert, onWahl]);

  if (!offen) return null;
  return createPortal(
    <div
      className="kvp-rd kvp-rd-scrim"
      style={{ zIndex: Z_LAYERS.windowConfirm }}
      onClick={() => !speichert && onWahl("abbrechen")}
      data-testid="ungespeichert-scrim"
    >
      <div
        className="dm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="ungespeichert-titel"
        aria-describedby="ungespeichert-text"
        style={{ width: "min(520px, 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dm-head">
          <div className="dm-head-top">
            <div className="dm-titles">
              <div className="dm-kicker">{texte.kicker}</div>
              <h2 id="ungespeichert-titel">{texte.titel}</h2>
            </div>
          </div>
        </div>
        <div className="dm-body" style={{ paddingTop: 4 }}>
          <p
            id="ungespeichert-text"
            style={{ margin: 0, font: '500 14.5px/1.5 "Hanken Grotesk", sans-serif', color: "#241522" }}
          >
            {texte.text}
          </p>
        </div>
        <div className="dm-foot">
          <button type="button" style={leise} disabled={speichert} onClick={() => onWahl("abbrechen")}>
            Abbrechen
          </button>
          <button type="button" style={knopf} disabled={speichert} onClick={() => onWahl("verwerfen")}>
            Verwerfen
          </button>
          <button
            ref={speichernRef}
            type="button"
            style={primaer}
            disabled={speichert}
            onClick={() => onWahl("speichern")}
          >
            {speichert ? "Speichert …" : texte.speichern}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

/**
 * Die Rückfrage als Promise. Eine zweite Frage, solange die erste offen ist,
 * bekommt dieselbe Antwort (zwei Wege gleichzeitig — etwa Zurück-Taste und
 * Fenster-X — sollen nicht zwei Dialoge stapeln).
 */
export function useUngespeichertFrage(texte?: UngespeichertTexte): {
  frage: () => Promise<UngespeichertWahl>;
  dialog: React.ReactNode;
  /** Während „Speichern" läuft, bleibt der Dialog mit gesperrten Knöpfen stehen. */
  setSpeichert: (an: boolean) => void;
  schliessen: () => void;
} {
  const [offen, setOffen] = React.useState(false);
  const [speichert, setSpeichert] = React.useState(false);
  const wartendRef = React.useRef<{ promise: Promise<UngespeichertWahl>; resolve: (w: UngespeichertWahl) => void } | null>(
    null,
  );

  const frage = React.useCallback((): Promise<UngespeichertWahl> => {
    if (wartendRef.current) return wartendRef.current.promise;
    let resolve!: (w: UngespeichertWahl) => void;
    const promise = new Promise<UngespeichertWahl>((r) => (resolve = r));
    wartendRef.current = { promise, resolve };
    setOffen(true);
    return promise;
  }, []);

  const onWahl = React.useCallback((wahl: UngespeichertWahl) => {
    const w = wartendRef.current;
    wartendRef.current = null;
    // Bei „Speichern" bleibt der Dialog offen, bis der Aufrufer fertig ist
    // (`schliessen`) — scheitert das Speichern, soll man sehen, wo man ist.
    if (wahl !== "speichern") setOffen(false);
    w?.resolve(wahl);
  }, []);

  const schliessen = React.useCallback(() => {
    setOffen(false);
    setSpeichert(false);
  }, []);

  return {
    frage,
    dialog: <UngespeichertDialog offen={offen} speichert={speichert} onWahl={onWahl} texte={texte} />,
    setSpeichert,
    schliessen,
  };
}
